/**
 * How a deposit reads on screen (Blueprint §6, §13, §14).
 *
 * Pure: strings in, strings out, no DOM — so the WORDING, which is the part
 * with rules attached, can be tested without a browser (same split as
 * proposal-copy.ts).
 *
 * The rules, restated because they are easy to lose:
 *
 *   · No shame. "Needs a look", never "you forgot to classify this".
 *   · Unknown is not income. A deposit nobody has answered for is shown as an
 *     open question, never as a type with a default.
 *   · The bank's label is a hint, shown small and acted on by nothing.
 *   · No dead controls: all five answers are always real and always work.
 */

import { formatMoney } from './envelope-math';
import type { ApiClassifyResult, ApiDeposit, ApiInflowType } from './api';

export interface InflowOption {
  type: ApiInflowType;
  /** On the button. Short enough that five fit a phone-width sheet. */
  label: string;
  /** One plain sentence under the question, for a screen reader and for doubt. */
  hint: string;
}

/**
 * The five answers, in the order they are offered. Income first: it is the
 * common case in a business that is paid. Order is the only nudge — no answer
 * is pre-selected, because pre-selecting one would be defaulting it.
 */
export const INFLOW_OPTIONS: readonly InflowOption[] = [
  { type: 'income', label: 'Income', hint: 'A customer or client paying you.' },
  { type: 'refund', label: 'Refund', hint: 'Money coming back from something you bought.' },
  { type: 'injection', label: 'I put it in', hint: 'Your own money going into the business.' },
  { type: 'financing', label: 'Loan', hint: 'Borrowed money. It is never set aside for tax.' },
  { type: 'transfer', label: 'My transfer', hint: 'Moving money between your own accounts.' },
];

const LABEL: Record<ApiInflowType, string> = {
  income: 'Income',
  refund: 'Refund',
  injection: 'Owner money in',
  financing: 'Loan',
  transfer: 'Own transfer',
};

/**
 * How an answer is said in a sentence. The chip labels are written to be tapped
 * ("I put it in", "My transfer"), and read aloud inside "Marked as ..." they come
 * out as "Marked as owner money in" — which a screen reader says with a straight face.
 */
const SAID_AS: Record<ApiInflowType, string> = {
  income: 'income',
  refund: 'a refund',
  injection: 'money you put in',
  financing: 'a loan',
  transfer: 'a transfer between your own accounts',
};

/** What a deposit is called on a chip. NULL is an open question, not a type. */
export function inflowLabel(type: ApiInflowType | null): string {
  return type == null ? 'Needs a look' : LABEL[type];
}

/**
 * The bank's own label, made readable. Advisory: we do not know what the bank
 * meant, so we say what it said and claim nothing about it.
 */
export function bankHintText(hint: string | null): string | null {
  if (hint == null) return null;
  const words = hint.replace(/_/g, ' ').trim().toLowerCase();
  if (words === '') return null;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Oct 4" from "2026-10-04". Built from the parts, never through Date: parsing a
 * bare ISO date as UTC and printing it in local time shows the previous day for
 * everyone west of Greenwich — a deposit would appear to land a day before it did.
 */
export function depositDateText(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  if (!m) return ymd;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}` : ymd;
}

/** Who it came from, or what the bank printed when it named nobody. */
export function depositTitle(d: Pick<ApiDeposit, 'counterparty' | 'description'>): string {
  return d.counterparty?.trim() || d.description;
}

/**
 * Why a deposit has the answer it has — shown beside the chip so a learned
 * answer is never mistaken for one the operator gave about THIS deposit.
 */
export function inflowSourceText(
  d: Pick<ApiDeposit, 'inflowType' | 'inflowSource'>,
): string | null {
  if (d.inflowType == null) return null;
  return d.inflowSource === 'counterparty' ? 'learned from this sender' : 'you chose this';
}

/** The set-aside beside a deposit, in words. NULL when there is nothing to say. */
export function skimText(d: Pick<ApiDeposit, 'skim'>): string | null {
  if (!d.skim) return null;
  const amount = formatMoney(d.skim.amountMinor);
  switch (d.skim.status) {
    case 'pending':
      return `${amount} set-aside waiting in Needs you`;
    case 'approved':
      return `${amount} set aside`;
    default:
      return null;
  }
}

/** The one line shown after an answer. States what changed, and nothing it cannot know. */
export function classifyMessage(r: ApiClassifyResult): string {
  const parts = [`Marked as ${SAID_AS[r.inflowType]}.`];

  if (r.inflowType === 'income' && r.skim?.status === 'pending') {
    parts.push(`A ${formatMoney(r.skim.amountMinor)} set-aside is waiting in Needs you.`);
  }
  if (r.withdrewSkim) parts.push('Its set-aside was withdrawn.');
  if (r.approvedSkimStays) parts.push('The set-aside you approved earlier stays as it is.');

  if (r.learnedFrom) {
    parts.push(
      r.appliedToOthers > 0
        ? `${r.appliedToOthers} other ${r.appliedToOthers === 1 ? 'deposit' : 'deposits'} from ${r.learnedFrom} got the same answer.`
        : `Future deposits from ${r.learnedFrom} will get the same answer.`,
    );
  }
  return parts.join(' ');
}

/**
 * How many things need the operator: the staged suggestions, the deposits
 * nobody has answered for and (v8.0.0) the bills waiting on an answer. One
 * queue, one number — never separate counts.
 */
export function needsYouCount(proposals: number, depositsToLookAt: number, bills = 0): number {
  return proposals + depositsToLookAt + bills;
}

/** Deposits that are an open question: posted money in with no answer yet. */
export function depositsNeedingALook(deposits: readonly ApiDeposit[]): ApiDeposit[] {
  return deposits.filter((d) => d.inflowType == null);
}
